import * as dmsQueryLanguage from './dms-query-language'
/**
 * Contains a set of properties that are used to control the language syntax
 * highlighting {@link monaco} features for a
 * {@link monaco.editor.IStandaloneCodeEditor}
 */
export class Language {
  /**
   * The dms query {@link Language} instance.
   */
  public static readonly DMS_QUERY = new Language({
    config: dmsQueryLanguage.CONFIG,
    documentFormattingEditProvider:
      dmsQueryLanguage.DOCUMENT_FORMATTING_EDIT_PROVIDER,
    monarch: dmsQueryLanguage.MONARCH,
    name: 'dms-query',
  })

  /**
   * List of all custom languages that will be registered.
   *
   * @see {@link monaco.languages.register}
   */
  public static readonly ALL = [this.DMS_QUERY]

  /**
   * Gets the existing {@link Language} with the given name, if there is one.
   * Otherwise, it assumes the name represents a new language and creates a new
   * {@link Language} for the given name
   */
  public static of(name: string): Language {
    return this.ALL.find(lang => lang.name === name) ?? new Language({ name })
  }

  /**
   * @see {@link monaco.languages.IMonarchLanguage}
   */
  public readonly monarch: monaco.languages.IMonarchLanguage | undefined

  /**
   * @see {@link monaco.languages.LanguageConfiguration}
   */
  public readonly config: monaco.languages.LanguageConfiguration | undefined

  /** Name of the {@link Language} */
  public readonly name: string

  /**
   * @see {@link Language.constructor.Params.documentFormattingEditProvider}
   */
  public readonly documentFormattingEditProvider?: monaco.languages.DocumentFormattingEditProvider

  private constructor({
    name,
    monarch = undefined,
    config = undefined,
    documentFormattingEditProvider = undefined,
  }: Language.constructor.Params) {
    this.monarch = monarch
    this.name = name
    this.config = config
    this.documentFormattingEditProvider = documentFormattingEditProvider
  }
}
export namespace Language {
  export namespace constructor {
    export interface Params {
      /**
       * The name of the language, used to identify it in Monaco
       *
       * @see {@link monaco.editor.setModelLanguage}
       */
      name: string
      /**
       * @see {@link monaco.languages.IMonarchLanguage}
       */
      monarch?: monaco.languages.IMonarchLanguage
      /** @see {@link monaco.languages.LanguageConfiguration} */
      config?: monaco.languages.LanguageConfiguration
      /**
       * Provider definition for formatting the document when `SHIFT + ALT + F` is pressed.
       * This configuration determines how the code will be formatted.
       *
       * @see {@link monaco.languages.DocumentFormattingEditProvider}
       */
      documentFormattingEditProvider?: monaco.languages.DocumentFormattingEditProvider
    }
  }
}
